import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { MarkerAnnotationItem, MarkerAnnotationModalData } from './marker-annotation-modal.model';
import { ApiClientService } from '@services/api-client/api-client.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-marker-annotation-modal',
    templateUrl: './marker-annotation-modal.component.html',
    styleUrls: ['./marker-annotation-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MarkerAnnotationModalComponent {
    private readonly modal = inject(NgbActiveModal);
    private readonly apiClientService = inject(ApiClientService);
    private readonly translateService = inject(TranslateService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    public data: MarkerAnnotationModalData;

    public isEditingMeasurementAnnotation = false;
    public isSavingMeasurementAnnotation = false;
    public measurementAnnotationDraft = '';

    private hasChanges = false;
    private readonly editingMarkers = new Set<number>();
    private readonly savingMarkers = new Set<number>();
    private readonly draftAnnotations = new Map<number, string>();

    public isEditing(marker: MarkerAnnotationItem): boolean {
        return this.editingMarkers.has(marker.number);
    }

    public isSaving(marker: MarkerAnnotationItem): boolean {
        return this.savingMarkers.has(marker.number);
    }

    public getDraftAnnotation(markerNumber: number): string {
        return this.draftAnnotations.get(markerNumber) ?? '';
    }

    public startEdit(marker: MarkerAnnotationItem): void {
        this.draftAnnotations.set(marker.number, marker.annotation ?? '');
        this.editingMarkers.add(marker.number);
        this.changeDetectorRef.markForCheck();
    }

    public cancelEdit(marker: MarkerAnnotationItem): void {
        this.editingMarkers.delete(marker.number);
        this.draftAnnotations.delete(marker.number);
        this.changeDetectorRef.markForCheck();
    }

    public onDraftChange(markerNumber: number, value: string): void {
        this.draftAnnotations.set(markerNumber, value);
    }

    public startEditMeasurementAnnotation(): void {
        this.measurementAnnotationDraft = this.data.annotation ?? '';
        this.isEditingMeasurementAnnotation = true;
        this.changeDetectorRef.markForCheck();
    }

    public cancelEditMeasurementAnnotation(): void {
        this.isEditingMeasurementAnnotation = false;
        this.changeDetectorRef.markForCheck();
    }

    public saveMeasurementAnnotation(): void {
        this.isSavingMeasurementAnnotation = true;
        this.changeDetectorRef.markForCheck();

        this.apiClientService
            .updateAnnotation$(this.data.measurementId, { annotation: this.measurementAnnotationDraft })
            .subscribe({
                next: () => {
                    this.data.annotation = this.measurementAnnotationDraft;
                    this.isSavingMeasurementAnnotation = false;
                    this.isEditingMeasurementAnnotation = false;
                    this.hasChanges = true;
                    this.changeDetectorRef.markForCheck();
                },
                error: () => {
                    this.isSavingMeasurementAnnotation = false;
                    this.changeDetectorRef.markForCheck();
                }
            });
    }

    public onClose(): void {
        this.modal.close(this.hasChanges);
    }

    public saveMarkerAnnotation(marker: MarkerAnnotationItem): void {
        const annotation = this.draftAnnotations.get(marker.number) ?? '';
        this.savingMarkers.add(marker.number);
        this.changeDetectorRef.markForCheck();

        this.apiClientService
            .updateMarkerAnnotation$(this.data.measurementId, marker.number, { annotation })
            .subscribe({
                next: () => {
                    marker.annotation = annotation;
                    this.savingMarkers.delete(marker.number);
                    this.editingMarkers.delete(marker.number);
                    this.draftAnnotations.delete(marker.number);
                    this.hasChanges = true;
                    this.changeDetectorRef.markForCheck();
                },
                error: () => {
                    this.savingMarkers.delete(marker.number);
                    this.changeDetectorRef.markForCheck();
                }
            });
    }

    /**
     * Format seconds as HH:mm:ss
     */
    public formatTime(totalSeconds: number): string {
        const seconds = Math.floor(totalSeconds);
        const hours = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
}
